import { APP_BASE_HREF, CurrencyPipe, DOCUMENT } from '@angular/common';
import {
  NgModule,
  CUSTOM_ELEMENTS_SCHEMA,
  APP_INITIALIZER,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { environment } from '../environments/environment';

import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { IntegrationService } from './shared/services/integration.service';
import { AuthWrapperService } from './shared/services/AuthWrapper.service';
import { AccountInfoModule } from './account-info-header/account-info.module';
import { RouterState, StoreRouterConnectingModule } from '@ngrx/router-store';
import { LandingPageComponent } from './landing-page/landing-page.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { APP_SETTINGS, CookieService, TokenHttpInterceptor } from '@charlesschwab/as-ui-core';
import { AuthenticationService, CoreService, TabHeaderModule, IMfeProps } from '@charlesschwab/sac-ui-core';
import { AppService } from './app.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from './shared/shared.module';
import { CreateRequestModule } from './create-request/create-request.module';
import { SetupEAuthorizationModule } from './setup-eAuthorization/setup-eAuthorization.module';
import { HeadersService } from './shared/utilities/HeadersService';
import * as accountInfoReducers from './account-info-header/reducers';
import * as createRequestReducers from './create-request/reducers';
import * as eAuthorizationReducers from './setup-eAuthorization/reducers';
import * as featureFlagReducers from './feature-flags/reducers';
import * as transactionViewReducers from './transaction-view/reducers';
import * as sharedStateReducers from './shared/reducers';
import * as standingAuthorizationReducers from './standing-authorizations/reducers';
import * as userPreferenceReducers from './user-preference/reducers';
import {
  FeatureToggleConfigurationProperties,
  FEATURE_TOGGLE_CONFIGURATION_PROPERTIES,
  SchwabFeatureToggleAngularModule,
} from '@schwab/schwab-feature-toggle-angular';
import { FeatureFlagService } from './shared/services/feature-flag-service';
import { FeatureFlagModule } from './feature-flags/feature-flags.module';
import { TransactionViewModule } from './transaction-view/transaction-view.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { AuthorizeModule } from './authorize/authorize.module';
import { ActionCenterRedirectService } from './shared/services/ActionCenterRedirect.service';
import { StandingAuthorizationsModule } from './standing-authorizations/standing-authorizations.module';
import { currentContextUpdated, initializePage } from './shared/actions/shared.actions';
import { UserPreferenceModule } from './user-preference/user-preference.module';
import { CorrelIdInterceptor } from './shared/correlid-interceptor';
import { DrupalFormService } from './shared/services/DrupalForm.service';
import { ModifiedFirstPartiesModule } from './modified-first-parties/modified-first-parties.module';
import { InitiateAndReleaseModule } from './initiate-and-release/initiate-and-release.module';
import { ValidateWireFlagsService } from './shared/services/validate-WireFlags.service';
import { singleSpaPropsSubject } from '../single-spa/single-spa-props';
import { getSingleSpaExtraProviders } from 'single-spa-angular';
import * as initReleaseReducers from '././initiate-and-release/reducers'
import { FormatIsoAddressFieldsService } from './shared/services/format-isoAddressFields.service';
import { initialAccountInfoState } from './account-info-header/reducers/account-info.reducers';

export const reducers = {
  account: accountInfoReducers.reducers,
  createRequest: createRequestReducers.reducers,
  setupEAuthorization: eAuthorizationReducers.reducers,
  featureFlags: featureFlagReducers.reducers,
  transactionView: transactionViewReducers.reducers,
  sharedState: sharedStateReducers.reducers,
  standingAuthorization: standingAuthorizationReducers.reducers,
  userPreference: userPreferenceReducers.reducers,
  initReleaseState: initReleaseReducers.reducers
};

export function initializeAppFactory(appService: AppService) {
  return (): Promise<any> => {
    return appService.initializeApp();
  };
}

export function initializeFeatureFlagFactory(
  featureFlagService: FeatureFlagService
) {
  return (): Promise<any> => {
    return featureFlagService.initializeFeatureFlags();
  };
}

export function initializeFeatureFlagConfig(
  appService: AppService
): FeatureToggleConfigurationProperties {
    return appService.setClientSideId();
  };

export function initializeAppMeta(reducer: any) {
  return function (state: any, action: any) {
    if (action.type == initializePage.type){
      state = {
        account: {
          info: state.account.info
        },
        createRequest: undefined,
        initRelease: undefined,
        setupEAuthorization: undefined,
        featureFlags: state.featureFlags,
        transactionView: undefined,
        sharedState: {
          step: undefined,
          scrollTo: undefined,
          displayCancelModal: undefined,
          standingAuthTableState: {
            actionsLinkInfo: state.sharedState.standingAuthTableState.actionsLinkInfo,
            loading: undefined,
            error: undefined,
            hasError: undefined,
            gridReady: undefined,
            isInitialLoad: undefined
          },
          reviewPage: undefined,
          confirmationPage: undefined,
          viewPage: undefined,
          previousCreateTypeInContext: state.sharedState.previousCreateTypeInContext
        },
        standingAuthorizationState: undefined,
        authorizeKey: state.authorizeKey,
        userPreferences: state.userPreferences
      }
    } else if (action.type === currentContextUpdated.type) {
      state = {
        account: {
          info: {
            ...initialAccountInfoState,
            userName: state.account.info.userName,
            classifications: {
              ...initialAccountInfoState.classifications,
              isDbsUser: state.account.info.classifications.isDbsUser,
              isEmployee: state.account.info.classifications.isEmployee,
            }
          },
          cashBalances: undefined,
          taxes: undefined, 
          headerState: undefined,
          transactions: undefined,
          submitCancel: undefined,
          templates: undefined,
          submitDelete: undefined,
          editTweModalState: undefined
        },
        createRequest: undefined,
        initRelease: undefined,
        setupEAuthorization: undefined,
        featureFlags: state.featureFlags,
        transactionView: undefined,
        sharedState: {
          createTypeInContext: state.sharedState.createTypeInContext
        },
        standingAuthorizationState: undefined,
        authorizeKey: state.authorizeKey,
        userPreferences: state.userPreferences
      }
    }

    return reducer(state, action);
  };
}

function coreServiceFactory() {
  var coreService: any;
  singleSpaPropsSubject.subscribe((props: IMfeProps) => { coreService = props.coreService; });
  return coreService;
}

function appSettingsFactory() {
  var appSettings: any;
  singleSpaPropsSubject.subscribe((props: IMfeProps) => { appSettings = props.appSettings; });
  return appSettings;
}

@NgModule({
  declarations: [
    AppComponent,
    LandingPageComponent,
    PageNotFoundComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    AccountInfoModule,
    CreateRequestModule,
    SetupEAuthorizationModule,
    StandingAuthorizationsModule,
    FeatureFlagModule,
    TransactionViewModule,
    AuthorizeModule,
    BrowserAnimationsModule,
    UserPreferenceModule,
    ModifiedFirstPartiesModule,
    TabHeaderModule,
    ToastrModule.forRoot({
      timeOut: 4250,
      closeButton: true,
      easing: 'ease-in-out',
      easeTime: 500,
      extendedTimeOut: 500,
      preventDuplicates: true
    }),
    StoreModule.forRoot(reducers, { metaReducers: [initializeAppMeta] }),
    StoreDevtoolsModule.instrument({
      name: 'Move Money NgRx store',
      maxAge: 25,
      logOnly: environment.production,
      autoPause: true,
    }),
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
      routerState: RouterState.Minimal,
    }),
    EffectsModule.forRoot([]),
    SchwabFeatureToggleAngularModule.forRoot(),
    InitiateAndReleaseModule
  ],
  providers: [
    getSingleSpaExtraProviders(),
    { provide: APP_SETTINGS, useFactory: appSettingsFactory },
    { provide: CoreService, useFactory: coreServiceFactory },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: Document, useExisting: DOCUMENT },
    IntegrationService,
    ActionCenterRedirectService,
    DrupalFormService,
    ValidateWireFlagsService,
    FormatIsoAddressFieldsService,
    CookieService,
    CurrencyPipe,
    AuthenticationService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [AppService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFeatureFlagFactory,
      deps: [FeatureFlagService],
      multi: true,
    },
    {
      provide: FEATURE_TOGGLE_CONFIGURATION_PROPERTIES,
      useFactory: initializeFeatureFlagConfig,
      deps: [AppService]
    },
    AuthWrapperService,
    HeadersService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenHttpInterceptor,
      multi: true,
      deps: [AuthenticationService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelIdInterceptor,
      multi: true,
    }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule { }
